import { defineStore } from 'pinia'
import fetchData, { HttpMethod } from '@/model/Storage'
import router from '@/router'
import { Offer, Contract, OfferState, ExtendedOffer, ExtendedContract } from '@/model/types'
import { Details } from '@/constants/offers'

// Centralized error handler
const handleError = (error: unknown, message: string) => {
    console.error(message, error)
}

export const useOfferStore = defineStore('OfferStore', {
    state: (): OfferState => ({
        offers: [],
        contracts: [],
        filteredContracts: [],
        loading: true,
        proposedOffer: {},
        selectedOffer: {},
        signaturesList: [],
        offersLinks: [],
    }),
    persist: true,
    actions: {
        resetOffers() {
            this.offers = []
        },

        async downloadPdf({ offerId }: { offerId: number }): Promise<void> {
            this.loading = true
            try {
                // Construct the file URL and trigger the download
                const fileURL = `/api/v1/offers/${offerId}/pdf`
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                document.body.appendChild(fileLink)
                fileLink.click()
            } catch (error) {
                handleError(error, 'Error downloading PDF:')
            } finally {
                this.loading = false
            }
        },

        async generatePdf({
            proposedOffer,
            offerId,
            contactId,
        }: {
            proposedOffer: Offer
            offerId: number
            contactId: number
        }): Promise<void> {
            this.loading = true
            try {
                console.log('proposedOffer', proposedOffer)
                await fetchData({
                    method: HttpMethod.POST,
                    endpoint: `/offers/${offerId}/pdf`,
                    body: JSON.stringify({ data: proposedOffer }),
                })
                await this.fetchOffers()
                router.push('/offers/schoolview/' + contactId)
            } catch (error) {
                handleError(error, 'Error generating PDF:')
            } finally {
                this.loading = false
            }
        },

        async selectOffer(
            offer: ExtendedOffer,
            contactId: number,
            contracts: ExtendedContract[]
        ): Promise<void> {
            offer.selectedSchoolType = offer.schoolType?.split('_')[0]
            offer.canton = offer.canton
            const detailPairs = Object.values(Details).flat()
            const matchingDetail = detailPairs.find((detail) => detail.key === offer.schoolType)
            offer.detail = matchingDetail ? matchingDetail.key : offer.schoolType
            offer.contract = contracts.find(
                (contract) =>
                    contract?.UUID === offer.calculator_function &&
                    contract?.Typ === offer.schoolType &&
                    Boolean(contract[offer.canton as keyof Contract])
            )
            this.selectedOffer = offer as Offer
            router.push(`/offers/createoffer/${contactId}/${offer.id}`)
        },

        async createOffer({ contactId, offer }: { contactId: number; offer: any }): Promise<any> {
            this.loading = true
            try {
                const response = await fetchData({
                    method: HttpMethod.POST,
                    endpoint: `contacts/${contactId}/offers/`,
                    body: JSON.stringify({ data: offer }),
                })
                this.proposedOffer = response.offer
                router.push('/offers/calculateprice/' + contactId)
                return response.offer
            } catch (error) {
                handleError(error, 'Error creating offer:')
                return null
            } finally {
                this.loading = false
            }
        },

        async fetchContracts(): Promise<ExtendedContract[]> {
            this.loading = true
            try {
                const response = await fetchData({ method: HttpMethod.GET, endpoint: 'contracts' })
                this.contracts = response.contracts
                return response.contracts
            } catch (error) {
                handleError(error, 'Error fetching contracts:')
                return []
            } finally {
                this.loading = false
            }
        },

        async fetchOffers(): Promise<void> {
            this.loading = true
            try {
                const response = await fetchData({
                    method: HttpMethod.GET,
                    endpoint: 'contacts/offers?limit=500&sort=contactId',
                })
                if (response) {
                    this.offers = response.offers
                    this.offersLinks = response.links
                } else {
                    throw new Error('Invalid response structure')
                }
            } catch (error) {
                handleError(error, 'Error fetching offers:')
            } finally {
                this.loading = false
            }
        },

        async fetchOffersLimit({
            limit,
            offset,
            filter,
            done,
        }: {
            limit: number
            offset: number
            filter: string
            done: Function
        }): Promise<void> {
            this.loading = true
            try {
                let endpoint = `contacts/offers?limit=${limit}&offset=${offset}${
                    filter ? `&search=${encodeURIComponent(filter)}` : '&sort=contactId'
                }`
                const response = await fetchData({ method: HttpMethod.GET, endpoint: endpoint })
                this.offers.push(...response.offers)
                done(response.offers.length === 0 ? 'empty' : 'ok')
            } catch (error) {
                handleError(error, 'Error in fetchOffersLimit:')
            } finally {
                this.loading = false
            }
        },

        async fetchOffersSearch({
            limit,
            offset,
            filter,
        }: {
            limit: number
            offset: number
            filter: string
        }): Promise<void> {
            this.loading = true
            try {
                let endpoint = `contacts/offers?limit=${limit}&offset=${offset}${
                    filter ? `&search=${encodeURIComponent(filter)}` : '&sort=contactId'
                }`
                console.log('Search endpoint:', endpoint)
                const response = await fetchData({ method: HttpMethod.GET, endpoint: endpoint })
                console.log('Search response:', response)
                if (response && response.offers) {
                    this.offers = response.offers
                } else {
                    console.warn('Invalid search response structure:', response)
                    this.offers = []
                }
            } catch (error) {
                handleError(error, 'Error in fetchOffersSearch:')
                this.offers = []
            } finally {
                this.loading = false
            }
        },

        async deleteOffers({ contactId }: { contactId: number }): Promise<void> {
            this.loading = true
            try {
                let index = this.offers.findIndex((item) => item.contactId == contactId)
                if (index !== -1) {
                    await fetchData({ method: HttpMethod.DELETE, endpoint: `contacts/${contactId}/offers/` })
                    this.offers.splice(index, 1)
                }
            } catch (error) {
                handleError(error, 'Error in deleteOffers:')
            } finally {
                this.loading = false
            }
        },

        async deleteOffer({
            contactId,
            offerId,
        }: {
            contactId: number
            offerId: number
        }): Promise<void> {
            this.loading = true
            try {
                let offers = this.offers.find((item) => item.contactId == contactId)
                if (offers) {
                    let currentOffers = offers.offers
                    if (currentOffers) {
                        await fetchData({
                            method: HttpMethod.DELETE,
                            endpoint: `/offers/${offerId}/`,
                        })
                        const index = currentOffers.findIndex((item) => item.id === offerId)
                        if (index !== -1) {
                            currentOffers.splice(index, 1)
                            this.offers.find((item) => item.contactId == contactId)!.offers =
                                currentOffers
                        }
                        if (currentOffers.length <= 0) {
                            router.push('/offers')
                        } else {
                            location.reload()
                        }
                    }
                }
            } catch (error) {
                handleError(error, 'Error deleting offer:')
            } finally {
                this.loading = false
            }
        },

        async fetchSignaturesList(): Promise<void> {
            this.loading = true
            try {
                const response = await fetchData({ method: HttpMethod.GET, endpoint: 'signatures_list' })
                this.signaturesList = response.signatures
            } catch (error) {
                handleError(error, 'Error fetching signatures list:')
            } finally {
                this.loading = false
            }
        },

        async clearSelectedOfferStore(): Promise<void> {
            this.selectedOffer = {}
        },

        async updateOffer({
            contactId,
            offerId,
            offer,
        }: {
            contactId: number
            offerId: number
            offer: any
        }): Promise<void> {
            this.loading = true
            try {
                await fetchData({
                    method: HttpMethod.PUT,
                    endpoint: `contacts/${contactId}/offers/${offerId}/`,
                    body: JSON.stringify({ data: offer }),
                })
                router.push('/offers/schoolview/' + contactId)
            } catch (error) {
                handleError(error, 'Error updating offer:')
            } finally {
                this.loading = false
            }
        },

        async fetchOfferAccess({ offerId }: { offerId: number }): Promise<any> {
            this.loading = true
            try {
                console.log(`Fetching access for offer ID: ${offerId}`)
                const response = await fetchData({
                    method: HttpMethod.GET,
                    endpoint: `offers/${offerId}/access`,
                })
                console.log('Raw API response:', response)
                let processedResponse
                if (!response) {
                    console.warn('Received null/undefined API response')
                    processedResponse = { roles: [] }
                } else if (response.roles) {
                    processedResponse = response
                } else if (response.data && response.data.roles) {
                    processedResponse = { roles: response.data.roles }
                } else {
                    console.warn('Unrecognized response structure:', response)
                    processedResponse = { roles: [] }
                }
                console.log('Processed response:', processedResponse)
                return processedResponse
            } catch (error) {
                handleError(error, `Error fetching offer access for ID ${offerId}:`)
                return { roles: [] }
            } finally {
                this.loading = false
            }
        },

        async updateOfferAccess(offerId: number, roleIds: number[]): Promise<void> {
            this.loading = true
            try {
                console.log('Updating offer access - offer ID:', offerId, 'role IDs:', roleIds)
                const validRoleIds = Array.isArray(roleIds) ? roleIds : []
                if (!validRoleIds.includes(33)) {
                    console.log('Note: Anykey role (33) is being removed from access')
                }
                await fetchData({
                    method: HttpMethod.PUT,
                    endpoint: `offers/${offerId}/access`,
                    body: JSON.stringify({ data: { roles: validRoleIds } }),
                })
                console.log('Offer access update completed successfully')
            } catch (error) {
                handleError(error, 'Error updating offer access:')
            } finally {
                this.loading = false
            }
        },

        async getHateoasLinks(): Promise<any[]> {
            this.loading = true
            try {
                const response = await fetchData({
                    method: HttpMethod.GET,
                    endpoint: 'offers/links',
                })
                console.log('HATEOAS API response:', response)
                return response.links || []
            } catch (error) {
                handleError(error, 'Error fetching HATEOAS links:')
                return []
            } finally {
                this.loading = false
            }
        },

        // NEW ACTION: Fetch third party partner information.
        async fetchThirdPartyPartners(): Promise<any[]> {
            this.loading = true
            try {
                const response = await fetchData({
                    method: HttpMethod.GET,
                    endpoint: 'offers/third_party_partners',
                })
                console.log('Third parties', response)
                return response.partners || []
            } catch (error) {
                handleError(error, 'Error fetching third party partners:')
                return []
            } finally {
                this.loading = false
            }
        },
    },
})
